import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {
    Checkbox,
    FormControlLabel,
    InputAdornment,
    OutlinedInput,
    Table, TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, TableSortLabel,
    Typography
} from "@mui/material";
import React, {useEffect, useState} from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import $ from "jquery";
import apiURL from "../../config/environment";
import {useNavigate} from "react-router-dom";
import {formatDate, orderAdminRowsBy} from "../../utils";
import {AddScheduleDialog} from "./addScheduleDialog";
import {EditScheduleDialog} from "./editScheduleDialog";
import {OrderIcon} from "../icons/OrderIcon";
import {isSameDay} from "date-fns";
import EventBusy from "@mui/icons-material/EventBusy";
import Copyright from "../copyright";

export const SchedulesTab = () => {
    const navigate = useNavigate();
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("name");
    const [openAddScheduleDialog, setOpenAddScheduleDialog] = React.useState(false);
    const [rowToEdit, setRowToEdit] = React.useState(null);
    const [showArchived, setShowArchived] = React.useState(true);

    const [searchText, setSearchText] = React.useState("");
    const [rows, setRows] = React.useState([]);
    const [rowsToDisplay, setRowsToDisplay] = React.useState([]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    }

    useEffect(() => {
        const token = JSON.parse(localStorage.getItem("userInfo"))?.token;
        $.ajax({
            method: "GET",
            url: apiURL + "admin/schedules",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .done((data) => {
                setRows(data);
            })
            .fail((res) => {
                if (res.status === 401) {
                    localStorage.removeItem("userInfo");
                    navigate("/login");
                }
            });
    }, [])

    useEffect(() => {
        let toDisplay = rows.map((row) => {
            const allEndDatesPassed = row.ranges.every((range) => {
                return range.endDate && !isSameDay(new Date(), new Date(range.endDate)) && new Date(range.endDate) < new Date();
            })
            return {
                ...row,
                archived: allEndDatesPassed,
            }
        })
        if (searchText.length > 0) {
            toDisplay = toDisplay.filter((row) => {
                return row.name.toLowerCase().includes(searchText.toLowerCase())
            })
        }

        if (!showArchived) {
            toDisplay = toDisplay.filter((row) => !row.archived);
        }

        if (orderBy === "SCATS" || orderBy === "ITERIS" || orderBy === "ECOCOUNTER") {
            toDisplay = sortScheduleRowsBySite(toDisplay, orderBy, order);
        } else if (orderBy.startsWith("period-")) {
            const periodIndex = parseInt(orderBy.split("-")[1], 10);
            toDisplay = sortScheduleRowsByPeriod(toDisplay, periodIndex, order);
        } else {
            orderAdminRowsBy(toDisplay, orderBy, order, setRowsToDisplay);
            return;
        }

        setRowsToDisplay(toDisplay)
    }, [rows, searchText, orderBy, order, showArchived])

    const handleClearSearch = () => {
        setSearchText("");
    }

    const handleSearch = (event) => {
        setSearchText(event.target.value);
    }

    return (
        <Box sx={{ padding: "36px 32px 16px 32px" }}>
            <Card sx={{borderRadius: "8px"}}>
                <CardContent sx={{padding: "16px 32px" }}>

                    <Box
                        sx={{
                            margin: "16px 0",
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >

                        <Box display={"flex"} alignItems={"center"} gap={"32px"}>
                            <Typography variant="h5" component="h3">
                                Schedules
                            </Typography>

                            <OutlinedInput
                                id="outlined-adornment-weight"
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                endAdornment={
                                    searchText.length > 0 && (
                                        <InputAdornment position="end" onClick={handleClearSearch}>
                                            <IconButton>
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                                placeholder={"Search"}
                                sx={{
                                    width: "300px",
                                    height: "40px",
                                }}
                                onChange={handleSearch}
                                value={searchText}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showArchived}
                                        onChange={(e) => setShowArchived(e.target.checked)}
                                    />
                                }
                                label="Show Completed"
                            />
                        </Box>


                        <Box display={"flex"} alignItems={"center"}>
                            <Button
                                variant={"contained"}
                                startIcon={<AddIcon />}
                                sx={{
                                    textTransform: "none",
                                    backgroundColor: "rgba(19, 151, 203, 1)",
                                    borderRadius: "6px",
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    letterSpacing: "0.15px",
                                }}
                                onClick={() => setOpenAddScheduleDialog(true)}
                            >
                                {"New Schedule"}
                            </Button>
                            <AddScheduleDialog
                                open={openAddScheduleDialog}
                                handleClose={() => setOpenAddScheduleDialog(false)}
                                handleSave={() => setOpenAddScheduleDialog(false)}
                                existingRows={rows}
                                setExistingRows={setRows}
                            />
                            <EditScheduleDialog
                                existingRows={rows}
                                setExistingRows={setRows}
                                handleClose={() => setRowToEdit(null)}
                                rowToEdit={rowToEdit}
                            />
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant={"body2"} sx={{ color: "grey" }}>
                            {rowsToDisplay.length} items
                        </Typography>
                    </Box>

                    <Box>
                        <TableContainer
                            sx={{
                                height: "calc(100vh - 294px)",
                                minHeight: "500px",
                                overflowX: "auto",
                                marginTop: "8px",
                            }}
                        >
                            <Table stickyHeader>
                                <TableHead sx={{ backgroundColor: "rgba(250, 250, 250, 1)" }}>
                                    <TableHeadRow data={rowsToDisplay} orderBy={orderBy} order={order} handleRequestSort={handleRequestSort} />
                                </TableHead>
                                <TableBody>
                                    <TableRows data={rowsToDisplay} setEditRowId={setRowToEdit} />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </CardContent>
            </Card>
            <Copyright />
        </Box>

    )
}

const TableHeadRow = ({ data, orderBy, order, handleRequestSort }) => {

    const maxPeriodSlots = Math.max(...data.map((row) => row.ranges.length));
    const periodSlots = Array.from({ length: maxPeriodSlots }, (_, index) => index);
    const sitesIncluded = new Set()
    for (const row of data) {
        for (const site of row.sites) {
            sitesIncluded.add(site.type);
        }
    }

    return (
        <TableRow>
            <TableCell
                key={"archived"}
                sortDirection={orderBy === "archived" ? order : false}
                padding={"normal"}
                sx={{
                    width: "1%",
                    whiteSpace: "nowrap",
                    backgroundColor: "rgba(250, 250, 250, 1)",
                    "& path": {
                        fill: orderBy === "archived" ? "black" : "none",
                        fillOpacity: "0.56",
                    },
                    "&:hover path": {
                        fill: orderBy === "archived" ? "black" : "grey",
                        fillOpacity: "0.56",
                        transition: "fill 0.2s",
                    },
                }}
            >
                <CustomTableSortLabel
                    label={""}
                    handleRequestSort={handleRequestSort}
                    id={"archived"}
                    orderBy={orderBy}
                    order={order}
                />
            </TableCell>
            <TableCell
                key={"name"}
                sortDirection={orderBy === "name" ? order : false}
                padding={"normal"}
                sx={{
                    width: "auto",
                    whiteSpace: "nowrap",
                    backgroundColor: "rgba(250, 250, 250, 1)",
                    "& path": {
                        fill: orderBy === "name" ? "black" : "none",
                        fillOpacity: "0.56",
                    },
                    "&:hover path": {
                        fill: orderBy === "name" ? "black" : "grey",
                        fillOpacity: "0.56",
                        transition: "fill 0.2s",
                    },
                }}
            >

                <CustomTableSortLabel
                    label={"Name"}
                    handleRequestSort={handleRequestSort}
                    id={"name"}
                    orderBy={orderBy}
                    order={order}
                />
                </TableCell>
            {sitesIncluded.has("SCATS") ? (
                <TableCell
                    key={"SCATS"}
                    sortDirection={orderBy === "SCATS" ? order : false}
                    padding={"normal"}
                    sx={{
                        width: "1%",
                        whiteSpace: "nowrap",
                        backgroundColor: "rgba(250, 250, 250, 1)",
                        "& path": {
                            fill: orderBy === "SCATS" ? "black" : "none",
                            fillOpacity: "0.56",
                        },
                        "&:hover path": {
                            fill: orderBy === "SCATS" ? "black" : "grey",
                            fillOpacity: "0.56",
                            transition: "fill 0.2s",
                        },
                    }}
                >
                    <CustomTableSortLabel
                        label={"Intersection"}
                        handleRequestSort={handleRequestSort}
                        id={"SCATS"}
                        orderBy={orderBy}
                        order={order}
                    />
                </TableCell>) : null}
            {sitesIncluded.has("ITERIS") ? (
                <TableCell
                    key={"ITERIS"}
                    sortDirection={orderBy === "ITERIS" ? order : false}
                    padding={"normal"}
                    sx={{
                        width: "1%",
                        whiteSpace: "nowrap",
                        backgroundColor: "rgba(250, 250, 250, 1)",
                        "& path": {
                            fill: orderBy === "ITERIS" ? "black" : "none",
                            fillOpacity: "0.56",
                        },
                        "&:hover path": {
                            fill: orderBy === "ITERIS" ? "black" : "grey",
                            fillOpacity: "0.56",
                            transition: "fill 0.2s",
                        },
                    }}
                >
                    <CustomTableSortLabel
                        label={"Segment"}
                        handleRequestSort={handleRequestSort}
                        id={"ITERIS"}
                        orderBy={orderBy}
                        order={order}
                    />
                </TableCell>) : null}
            {sitesIncluded.has("ECOCOUNTER") ? (
                <TableCell
                    key={"ECOCOUNTER"}
                    sortDirection={orderBy === "ECOCOUNTER" ? order : false}
                    padding={"normal"}
                    sx={{
                        width: "1%",
                        whiteSpace: "nowrap",
                        backgroundColor: "rgba(250, 250, 250, 1)",
                        "& path": {
                            fill: orderBy === "ECOCOUNTER" ? "black" : "none",
                            fillOpacity: "0.56",
                        },
                        "&:hover path": {
                            fill: orderBy === "ECOCOUNTER" ? "black" : "grey",
                            fillOpacity: "0.56",
                            transition: "fill 0.2s",
                        },
                    }}
                >
                    <CustomTableSortLabel
                        label={"Bike Counter"}
                        handleRequestSort={handleRequestSort}
                        id={"ECOCOUNTER"}
                        orderBy={orderBy}
                        order={order}
                    />
                </TableCell>) : null}
            {periodSlots.map((_, index) => {
                return (
                    <TableCell
                        sortDirection={orderBy === `period-${index}` ? order : false}
                        padding={"normal"}
                        key={`period-${index}`}
                        sx={{
                            width: "1%",
                            whiteSpace: "nowrap",
                            backgroundColor: "rgba(250, 250, 250, 1)",
                            "& path": {
                                fill: orderBy === "period-" + index ? "black" : "none",
                                fillOpacity: "0.56",
                            },
                            "&:hover path": {
                                fill: orderBy === "period-" + index ? "black" : "grey",
                                fillOpacity: "0.56",
                                transition: "fill 0.2s",
                            },
                        }}
                    >
                        <CustomTableSortLabel
                            label={`Period ${index + 1}`}
                            handleRequestSort={handleRequestSort}
                            id={"period-" + index}
                            orderBy={orderBy}
                            order={order}
                        />

                    </TableCell>
                )
            })}
            <TableCell
                padding={"normal"}
                sortDirection={orderBy === "lastModified" ? order : false}
                sx={{
                    width: "1%",
                    whiteSpace: "nowrap",
                    backgroundColor: "rgba(250, 250, 250, 1)",
                    "& path": {
                        fill: orderBy === "lastModified" ? "black" : "none",
                        fillOpacity: "0.56",
                    },
                    "&:hover path": {
                        fill: orderBy === "lastModified" ? "black" : "grey",
                        fillOpacity: "0.56",
                        transition: "fill 0.2s",
                    },
                }}
                key={"lastModified"}
                >
                <CustomTableSortLabel
                    label={"Last Modified"}
                    handleRequestSort={handleRequestSort}
                    id={"modifiedDate"}
                    orderBy={orderBy}
                    order={order}
                />
            </TableCell>
            <TableCell
                padding={"normal"}
                sx={{
                    width: "1%",
                    whiteSpace: "nowrap",
                    backgroundColor: "rgba(250, 250, 250, 1)",
                    "& path": {
                        fill: orderBy === "modifiedBy" ? "black" : "none",
                        fillOpacity: "0.56",
                    },
                    "&:hover path": {
                        fill: orderBy === "modifiedBy" ? "black" : "grey",
                        fillOpacity: "0.56",
                        transition: "fill 0.2s",
                    },
                }}
                key={"modifiedBy"}
                sortDirection={orderBy === "modifiedBy" ? order : false}
                >
                <CustomTableSortLabel
                    label={"Modified By"}
                    handleRequestSort={handleRequestSort}
                    id={"modifiedBy"}
                    orderBy={orderBy}
                    order={order}
                />
            </TableCell>
        </TableRow>
    )
}

const TableRows = ({data, setEditRowId}) => {

    const sitesIncluded = new Set()
    for (const row of data) {
        for (const site of row.sites) {
            sitesIncluded.add(site.type);
        }
    }

    const maxPeriodSlots = Math.max(...data.map((row) => row.ranges.length));
    const periodSlots = Array.from({ length: maxPeriodSlots }, (_, index) => index);

    return (
        data.map((row) => {
            return (
                <TableRow
                    hover
                    onClick={() => {
                        setEditRowId(row)
                    }}
                    sx={{
                        cursor: "pointer",
                        "&:not(:hover) td": {
                            color: row.archived ? "#888" : "",
                        },
                    }}
                    key={row.id}>
                    <TableCell padding={"none"} align={"center"}>
                        {row.archived ? (
                            <EventBusy
                                titleAccess={"Hidden"}
                                fontSize={"small"}
                                sx={{ verticalAlign: "middle", opacity: "0.8" }}
                              />
                        ) : null}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{row.name}</TableCell>
                    {sitesIncluded.has("SCATS") ? (
                        <TableCell>{row.sites.filter((site) =>
                            site.type === "SCATS"
                        ).map((site) => site.siteId).sort((a, b) => a - b).join(", ")}</TableCell>
                    ) : null}
                    {sitesIncluded.has("ITERIS") ? (
                        <TableCell>{row.sites.filter((site) =>
                            site.type === "ITERIS"
                        ).map((site) => site.siteId).sort((a, b) => a - b).join(", ")}</TableCell>
                    ) : null}
                    {sitesIncluded.has("ECOCOUNTER") ? (
                        <TableCell>{row.sites.filter((site) =>
                            site.type === "ECOCOUNTER"
                        ).map((site) => site.siteId).sort((a, b) => a - b).join(", ")}</TableCell>
                    ) : null}

                    {periodSlots.map((_, index) => {
                            if (row.ranges[index]) {
                                return (
                                    <TableCell sx={{ whiteSpace: "nowrap" }} key={`period-${index}`}>
                                        {formatDate(new Date(row.ranges[index].startDate))} –  {row.ranges[index].endDate ? formatDate(new Date(row.ranges[index].endDate)) : "?"}
                                    </TableCell>
                                )
                            } else {
                                return (
                                    <TableCell key={`period-${index}`}></TableCell>
                                )
                            }
                    })}
                    <TableCell>{row.modifiedDate ? formatDate(new Date(row.modifiedDate)) : ""}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{row.modifiedBy}</TableCell>
                </TableRow>
            )
        }
    ));
}

const CustomTableSortLabel = ({id, handleRequestSort, label, orderBy, order}) => {
    return (
    <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => handleRequestSort(id)}
        IconComponent={() => (
            <IconButton
                sx={{
                    marginLeft: "4px",
                    padding: 0,
                    transition: "transform 0.2s",
                }}
                disableRipple
            >
                <OrderIcon order={order} />
            </IconButton>
        )}
    >
        {label}
    </TableSortLabel>
        )
}

const sortScheduleRowsBySite = (rows, siteType, order) => {
    return rows.sort((a, b) => {
        const aSites = a.sites ? a.sites.filter((s) => s.type === siteType) : [];
        const bSites = b.sites ? b.sites.filter((s) => s.type === siteType) : [];
        if (aSites.length === 0 && bSites.length === 0) {
            return 0;
        }
        if (aSites.length === 0) {
            return 1;
        }
        if (bSites.length === 0) {
            return -1;
        }

        const aSiteId = aSites[0].siteId;
        const bSiteId = bSites[0].siteId;
        return order === "asc" ? aSiteId - bSiteId : bSiteId - aSiteId;
    });
}

const sortScheduleRowsByPeriod = (rows, periodIndex, order) => {
    return rows.sort((a, b) => {
        const aPeriod = a.ranges ? a.ranges[periodIndex] : null;
        const bPeriod = b.ranges ? b.ranges[periodIndex] : null;

        if (!aPeriod && !bPeriod) {
            return 0;
        }
        if (!aPeriod) {
            return 1;
        }
        if (!bPeriod) {
            return -1;
        }

        const aStartDate = new Date(aPeriod.startDate);
        const bStartDate = new Date(bPeriod.startDate);
        return order === "asc" ? aStartDate - bStartDate : bStartDate - aStartDate;
    });
}
